/**
 * YieldPanel — Phase 8f-bcs (2026-05-07)
 * --------------------------------------
 * Three operator-curated rent scenarios (low / mid / high) shown
 * side-by-side. The "mid" column is highlighted with a subtle gold
 * background as the default reference point. Numbers are read
 * verbatim from the unit's rentScenarios — no rent-from-assumption
 * math is performed here.
 */
import { useT, type StringKey } from '@lib/i18n';
import { formatOMR, formatPercentValue, type RentScenarioKey } from '@lib/pricing';
import type { RentScenario, Unit } from '@lib/types';

interface YieldPanelProps {
  unit: Unit;
  /** Override the default highlighted scenario. Defaults to 'mid'. */
  highlight?: RentScenarioKey;
}

interface ColumnSpec {
  key: RentScenarioKey;
  emoji: string;
  titleKey: StringKey;
  scenario: RentScenario;
}

export default function YieldPanel({ unit, highlight = 'mid' }: YieldPanelProps) {
  const t = useT();
  const cols: ColumnSpec[] = [
    { key: 'low',  emoji: '🟢', titleKey: 'yield.low',  scenario: unit.rentScenarios.low },
    { key: 'mid',  emoji: '🟡', titleKey: 'yield.mid',  scenario: unit.rentScenarios.mid },
    { key: 'high', emoji: '🔴', titleKey: 'yield.high', scenario: unit.rentScenarios.high },
  ];

  return (
    <div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {cols.map((c) => (
          <ScenarioCard
            key={c.key}
            emoji={c.emoji}
            title={t(c.titleKey)}
            scenario={c.scenario}
            highlighted={c.key === highlight}
            t={t}
          />
        ))}
      </div>
      <p className="text-xs text-navy-400 mt-3 leading-relaxed">
        {t('match.assumptions')}
      </p>
    </div>
  );
}

function ScenarioCard({
  emoji,
  title,
  scenario,
  highlighted,
  t,
}: {
  emoji: string;
  title: string;
  scenario: RentScenario;
  highlighted: boolean;
  t: (k: StringKey) => string;
}) {
  const containerCls = highlighted
    ? 'border-gold/50 bg-gold-50 ring-1 ring-gold/30'
    : 'border-navy-100 bg-white';
  const titleCls = highlighted ? 'text-gold-700' : 'text-navy-600';
  return (
    <div className={`p-4 rounded-xl border ${containerCls} flex flex-col gap-2`}>
      <div className={`text-sm font-semibold ${titleCls}`}>
        <span className="me-1">{emoji}</span>
        {title}
      </div>

      <div className="text-2xl md:text-3xl font-bold text-navy leading-tight flex items-baseline gap-1 flex-wrap">
        <span className="num-en">
          {formatOMR(scenario.monthlyRent, { withSymbol: false, decimals: 0 })}
        </span>
        <span className="text-xs font-normal text-navy-500">
          {t('common.omr')}/{t('common.month')}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 text-xs">
        <Stat
          label={t('yield.yieldLabel')}
          value={<span className="num-en">{formatPercentValue(scenario.yieldPct)}</span>}
        />
        <Stat
          label={t('yield.paybackLabel')}
          value={
            <>
              <span className="num-en">{scenario.paybackYears.toFixed(1)}</span>
              <span className="text-navy-500 ms-1">{t('match.years')}</span>
            </>
          }
        />
      </div>

      {scenario.explainer && (
        <p className="text-[11px] italic text-navy-500 leading-relaxed border-t border-navy-100 pt-2 mt-1">
          {scenario.explainer}
        </p>
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div className="bg-white/40 rounded-md p-2">
      <div className="text-[10px] uppercase tracking-wide text-navy-500 mb-0.5">
        {label}
      </div>
      <div className="text-sm font-semibold text-navy">{value}</div>
    </div>
  );
}
